import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, ArrowUpRight, Copy, Check, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { ARTIKEL } from "@/data/artikel";
import { setPageMeta } from "@/utils/seo";
import { copyText } from "@/components/landing/ShareButtons";
import { trackEvent } from "@/utils/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useAllArticles = () => {
  const [articles, setArticles] = useState(ARTIKEL);
  useEffect(() => {
    axios.get(`${API}/artikel`).then((r) => {
      const statis = ARTIKEL.filter((a) => !r.data.some((b) => b.slug === a.slug));
      setArticles([...r.data, ...statis].sort((a, b) => (b.tanggal > a.tanggal ? 1 : -1)));
    }).catch(() => {});
  }, []);
  return articles;
};

const fmtDate = (d) => new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });

const ArticleShare = ({ artikel }) => {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/artikel/${artikel.slug}`;
  const text = `${artikel.judul}\n\n${artikel.metaDescription}`;
  const share = (href, kanal) => {
    trackEvent("share_artikel", { artikel: artikel.slug, kanal });
    window.open(href, "_blank", "noopener,noreferrer");
  };
  const copy = async () => {
    if (await copyText(`${text}\n\nBaca selengkapnya: ${url}`)) {
      setCopied(true);
      toast.success("Teks + link artikel disalin — siap ditempel ke grup!");
      trackEvent("share_artikel", { artikel: artikel.slug, kanal: "copy" });
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const cls = "inline-flex items-center gap-2 border border-[#211B10]/15 px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] text-[#211B10]/70 hover:border-[#A67C1E]/50 hover:text-[#A67C1E] transition-colors duration-300";
  return (
    <div className="mt-10 flex items-center gap-2 flex-wrap" data-testid="article-share-buttons">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#211B10]/45 mr-1">Bagikan:</span>
      <button data-testid="article-share-whatsapp" className={cls} onClick={() => share(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, "whatsapp")}>
        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
      </button>
      <button data-testid="article-share-telegram" className={cls} onClick={() => share(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, "telegram")}>
        <Send className="w-3.5 h-3.5" /> Telegram
      </button>
      <button data-testid="article-share-copy" className={cls} onClick={copy}>
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Tersalin" : "Salin Teks + Link"}
      </button>
    </div>
  );
};

export function ArticlesIndex() {
  const articles = useAllArticles();
  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta("Artikel — NALURA", "Artikel NALURA tentang pola pikir uang, fokus, ketenangan, dan pengembangan diri. Good Mind Good Life.");
  }, []);

  return (
    <div className="bg-[#FBF7EE] text-[#211B10] min-h-screen" data-testid="articles-page">
      <Nav />
      <main className="pt-32 md:pt-40 pb-24 max-w-5xl mx-auto px-6 md:px-12">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#A67C1E] mb-6">Artikel</p>
        <h1 className="font-display tracking-tighter text-4xl sm:text-5xl mb-16">Bacaan untuk pikiran yang bertumbuh.</h1>
        <div className="grid md:grid-cols-2 gap-10">
          {articles.map((a) => (
            <Link key={a.slug} to={`/artikel/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group border border-[#211B10]/10 bg-white hover:border-[#A67C1E]/40 transition-colors duration-500">
              <div className="overflow-hidden aspect-[16/9]">
                <img src={a.gambar} alt={a.judul} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A67C1E] mb-3">{a.kategori} · {fmtDate(a.tanggal)}</p>
                <h2 className="font-display text-2xl tracking-tight mb-3 group-hover:text-[#A67C1E] transition-colors">{a.judul}</h2>
                <p className="text-sm text-[#211B10]/60 leading-relaxed mb-5">{a.metaDescription}</p>
                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#211B10]/50 group-hover:text-[#A67C1E] transition-colors">
                  Baca Artikel <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function ArticleDetail() {
  const { slug } = useParams();
  const [fetched, setFetched] = useState(null);
  const statis = ARTIKEL.find((a) => a.slug === slug);
  const artikel = statis || fetched;

  useEffect(() => {
    if (!statis) {
      axios.get(`${API}/artikel/${slug}`).then((r) => setFetched(r.data)).catch(() => setFetched(false));
    }
  }, [slug, statis]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (artikel) setPageMeta(`${artikel.judul} — NALURA`, artikel.metaDescription);
  }, [artikel]);

  if (!artikel) {
    if (fetched !== false) {
      return <div className="min-h-screen bg-[#FBF7EE]" data-testid="article-loading" />;
    }
    return (
      <div className="min-h-screen bg-[#FBF7EE] text-[#211B10] flex flex-col items-center justify-center gap-6 px-6" data-testid="article-not-found">
        <p className="font-display text-3xl tracking-tight">Artikel tidak ditemukan</p>
        <Link to="/artikel" className="inline-flex items-center gap-2 text-[#A67C1E] font-mono text-xs uppercase tracking-[0.25em]">
          <ArrowLeft className="w-4 h-4" /> Semua Artikel
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-[#FBF7EE] text-[#211B10] min-h-screen" data-testid="article-detail-page">
      <Nav />
      <main className="pt-32 md:pt-40 pb-24 max-w-3xl mx-auto px-6 md:px-12">
        <Link to="/artikel" data-testid="article-back-link" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#211B10]/60 hover:text-[#A67C1E] transition-colors mb-10">
          <ArrowLeft className="w-3.5 h-3.5" /> Semua Artikel
        </Link>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A67C1E] mb-4">{artikel.kategori} · {fmtDate(artikel.tanggal)}</p>
        <h1 className="font-display tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-10">{artikel.judul}</h1>
        <img src={artikel.gambar} alt={artikel.judul} loading="lazy" className="w-full aspect-[16/9] object-cover border border-[#211B10]/10 mb-10" />
        <div className="space-y-6 text-base text-[#211B10]/70 leading-relaxed">
          {artikel.konten.map((p, i) => <p key={i}>{p}</p>)}
        </div>
        <ArticleShare artikel={artikel} />
        {artikel.ctaProduk && (
          <div className="mt-12 border border-[#A67C1E]/30 bg-[#A67C1E]/5 p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A67C1E] mb-3">Lanjutkan Perjalanan Anda</p>
            <p className="text-sm text-[#211B10]/70 mb-5">Siap melangkah lebih dalam? Lihat program pilihan kurator yang relevan dengan artikel ini.</p>
            <Link
              to={`/p/${artikel.ctaProduk}`}
              data-testid="article-cta-link"
              className="inline-flex items-center gap-2 bg-[#A67C1E] text-[#FBF7EE] font-medium text-xs uppercase tracking-[0.12em] px-6 py-3.5 hover:bg-[#211B10] transition-colors duration-300"
            >
              Lihat Program <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
